/**
 * 用户状态管理（Zustand）。
 *
 * 职责：管理当前用户的余额、近期用量概览等本地状态与加载状态。
 * 仅做本地状态管理，不在此发起 HTTP 请求（数据填充由 T04 的 services 完成）。
 *
 * @module store/userStore
 */
import { create } from 'zustand';
import type { UsageOverview } from '@/types';

/**
 * 空的用量概览占位（所有数值归零）。
 * 供 `clear` 与初始状态复用，避免重复字面量。
 */
export const EMPTY_USAGE_OVERVIEW: UsageOverview = {
  totalTokens: 0,
  totalCost: '0',
  requestCount: 0,
  dailyTrend: [],
};

/**
 * 用户 store 状态与动作定义。
 */
export interface UserState {
  /** 当前用户余额（字符串，单位：元）。 */
  balance: string;
  /** 近期用量概览占位。 */
  usageOverview: UsageOverview;
  /** 数据加载中标记。 */
  loading: boolean;
  /** 设置余额。 */
  setBalance: (balance: string) => void;
  /** 设置用量概览。 */
  setUsageOverview: (data: UsageOverview) => void;
  /** 设置加载状态。 */
  setLoading: (loading: boolean) => void;
  /** 清空用户状态（退出登录时调用）。 */
  clear: () => void;
}

/**
 * 用户 store。
 */
export const useUserStore = create<UserState>((set) => ({
  balance: '0',
  usageOverview: EMPTY_USAGE_OVERVIEW,
  loading: false,

  setBalance: (balance: string): void => {
    set({ balance });
  },

  setUsageOverview: (data: UsageOverview): void => {
    set({ usageOverview: data });
  },

  setLoading: (loading: boolean): void => {
    set({ loading });
  },

  clear: (): void => {
    set({
      balance: '0',
      usageOverview: EMPTY_USAGE_OVERVIEW,
      loading: false,
    });
  },
}));
